import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Collapse,
  ListItemButton,
  ListItemText,
  IconButton,
} from "@mui/material";
import { ExpandLess, ExpandMore, Refresh } from "@mui/icons-material";
import styled from "@emotion/styled";
import { useIntl } from "react-intl";
import ParkkiTheme from "themes/ParkkiTheme";
import AutomaticSystemForm from "./AutomaticSystemForm";
import AutomaticSystemsActioner from "services/gates/AutomaticSystems/actioner";
import { connect } from "react-redux";
import Abilities from "services/permissions/abilities";

const SystemName = styled.div`
  font-size: ${ParkkiTheme.fontSizes.large};
  font-weight: 600;
  color: ${ParkkiTheme.colors.darkGrey};
`;

const SystemMode = styled.div`
  font-size: ${ParkkiTheme.fontSizes.small};
  color: ${ParkkiTheme.colors.grey};
  background-color: ${ParkkiTheme.colors.lightGrey};
  padding: 8px 12px;
  border-radius: 8px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  min-height: 20px;
`;

const SystemModeError = styled.div`
  font-size: ${ParkkiTheme.fontSizes.small};
  color: ${ParkkiTheme.colors.red};
  background-color: #ffebee;
  padding: 8px 12px;
  border-radius: 8px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  min-height: 20px;
`;

const SystemStatus = styled.div`
  font-size: ${ParkkiTheme.fontSizes.small};
  color: ${ParkkiTheme.colors.grey};
  background-color: ${ParkkiTheme.colors.lightGrey};
  padding: 8px 12px;
  border-radius: 8px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 20px;
`;

const SystemStatusSuccess = styled.div`
  font-size: ${ParkkiTheme.fontSizes.small};
  color: #2e7d32;
  background-color: #e8f5e8;
  padding: 8px 12px;
  border-radius: 8px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 20px;
`;

const ReloadButton = styled(IconButton)`
  padding: 2px;
  margin: 0;

  .MuiSvgIcon-root {
    font-size: 16px;
    color: ${ParkkiTheme.colors.grey};
    transition: transform 0.3s ease;
    ${(props) =>
      props.isLoading &&
      `
      animation: spin 1s linear infinite;
    `}
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);

    .MuiSvgIcon-root {
      color: ${ParkkiTheme.colors.darkGrey};
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ReloadButtonSuccess = styled(IconButton)`
  padding: 2px;
  margin: 0;

  .MuiSvgIcon-root {
    font-size: 16px;
    color: #2e7d32;
    transition: transform 0.3s ease;
    ${(props) =>
      props.isLoading &&
      `
      animation: spin 1s linear infinite;
    `}
  }

  &:hover {
    background-color: rgba(46, 125, 50, 0.1);

    .MuiSvgIcon-root {
      color: #1b5e20;
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ReloadButtonError = styled(IconButton)`
  padding: 2px;
  margin: 0;

  .MuiSvgIcon-root {
    font-size: 16px;
    color: ${ParkkiTheme.colors.red};
    transition: transform 0.3s ease;
    ${(props) =>
      props.isLoading &&
      `
      animation: spin 1s linear infinite;
    `}
  }

  &:hover {
    background-color: rgba(229, 80, 57, 0.1);

    .MuiSvgIcon-root {
      color: ${ParkkiTheme.colors.red};
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const LeftContent = styled.div`
  display: flex;
  align-items: center;
`;

const RightContent = styled.div`
  display: flex;
  align-items: center;
`;

function AutomaticSystemItem({
  system,
  retrieveAutomaticSystemsDetails,
  automaticSystemsDetails,
}) {
  const intl = useIntl();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isOnError, setIsOnError] = useState(false);

  const getAutomaticSystemsDetails = useCallback(async () => {
    setIsOnError(false);
    try {
      await retrieveAutomaticSystemsDetails(system.id);
    } catch (error) {
      setIsOnError(true);
    }
  }, [system.id, retrieveAutomaticSystemsDetails]);

  useEffect(() => {
    getAutomaticSystemsDetails();
  }, [system.id, retrieveAutomaticSystemsDetails]);

  const systemDetails = useMemo(() => {
    if (
      automaticSystemsDetails?.[system.id] &&
      automaticSystemsDetails?.[system.id]?.loading === false
    ) {
      return automaticSystemsDetails?.[system.id]?.data;
    }
    return null;
  }, [automaticSystemsDetails, system.id]);

  const automaticSystemsLoading = useMemo(() => {
    return automaticSystemsDetails?.[system.id]?.loading ?? true;
  }, [automaticSystemsDetails, system.id]);

  const systemStatus = useMemo(() => {
    const { isClosed, isMoving } = systemDetails?.barrier ?? {};
    if (isClosed) {
      return intl.formatMessage({
        id: "automaticSystemsScene.statusValues.closed",
      });
    }
    if (isMoving) {
      return intl.formatMessage({
        id: "automaticSystemsScene.statusValues.moving",
      });
    }
    return intl.formatMessage({
      id: "automaticSystemsScene.statusValues.open",
    });
  }, [systemDetails, intl]);

  const getModeDisplayName = useMemo(() => {
    const modeCode = systemDetails?.operationMode?.name;
    const modeMap = {
      AUTO: intl.formatMessage({ id: "automaticSystemsScene.modes.AUTO" }),
      FREE: intl.formatMessage({ id: "automaticSystemsScene.modes.FREE" }),
      BLOP: intl.formatMessage({ id: "automaticSystemsScene.modes.BLOP" }),
      BLCL: intl.formatMessage({ id: "automaticSystemsScene.modes.BLCL" }),
    };
    return modeMap[modeCode] || modeCode || "Inconnu";
  }, [systemDetails, intl]);

  const handleClick = () => {
    if (!isOnError && canInteract) {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleReload = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    getAutomaticSystemsDetails();
  };

  const canViewDetails = Abilities.ability.can(
    "read",
    "config_gates.automaticSystemsDetails"
  );
  const canChangeStatus =
    Abilities.ability.can("read", "config_gates.automaticSystemsOpen") ||
    Abilities.ability.can("read", "config_gates.automaticSystemsClose") ||
    Abilities.ability.can(
      "read",
      "config_gates.automaticSystemsEnableEmergency"
    );
  const canChangeMode = Abilities.ability.can(
    "read",
    "config_gates.automaticSystemsMode"
  );
  const canInteract = canChangeStatus || canChangeMode;

  return (
    <>
      <ListItemButton onClick={handleClick}>
        <ListItemText
          primary={
            <HeaderContent>
              <LeftContent>
                <SystemName>{system.name}</SystemName>
              </LeftContent>
              <RightContent>
                {isOnError ? (
                  <SystemModeError>
                    <>
                      {intl.formatMessage({
                        id: "automaticSystemsScene.error.cannotRetrieveInfo",
                      })}{" "}
                      {canViewDetails && (
                        <ReloadButtonError
                          isLoading={automaticSystemsLoading}
                          onClick={handleReload}
                          onMouseDown={(e) => e.stopPropagation()}
                          onMouseUp={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                          onTouchEnd={(e) => e.stopPropagation()}
                        >
                          <Refresh />
                        </ReloadButtonError>
                      )}
                    </>
                  </SystemModeError>
                ) : (
                  <>
                    <SystemMode>
                      {automaticSystemsLoading
                        ? intl.formatMessage({
                            id: "automaticSystemsScene.loading",
                          })
                        : `${intl.formatMessage({
                            id: "automaticSystemsScene.mode",
                          })} ${getModeDisplayName}`}
                    </SystemMode>
                    <SystemStatusSuccess>
                      {automaticSystemsLoading
                        ? intl.formatMessage({
                            id: "automaticSystemsScene.loading",
                          })
                        : `${intl.formatMessage({
                            id: "automaticSystemsScene.status",
                          })}: ${systemStatus}`}
                      {canViewDetails && (
                        <ReloadButtonSuccess
                          isLoading={automaticSystemsLoading}
                          onClick={handleReload}
                          onMouseDown={(e) => e.stopPropagation()}
                          onMouseUp={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                          onTouchEnd={(e) => e.stopPropagation()}
                        >
                          <Refresh />
                        </ReloadButtonSuccess>
                      )}
                    </SystemStatusSuccess>
                  </>
                )}
              </RightContent>
            </HeaderContent>
          }
        />
        {!isOnError &&
          canInteract &&
          (isCollapsed ? <ExpandMore /> : <ExpandLess />)}
      </ListItemButton>
      <Collapse in={!isCollapsed && !isOnError} timeout="auto" unmountOnExit>
        <AutomaticSystemForm system={system} systemDetails={systemDetails} />
      </Collapse>
    </>
  );
}

const mapStateToProps = (state) => ({
  automaticSystemsDetails: state.AutomaticSystems.automaticSystemsDetails,
});

const mapDispatchToProps = (dispatch) => ({
  retrieveAutomaticSystemsDetails: (system) =>
    dispatch(AutomaticSystemsActioner.retrieveAutomaticSystemsDetails(system)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AutomaticSystemItem);
